import {
  createContext,
  ReactElement,
  ReactNode,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { v4 as uuidv4 } from 'uuid';
import { SnippetVarValues } from '../types';

export type SnippetScopeState = {
  vars: SnippetVarValues;

  /**
   * A UUID for this key, the same for every snippet in the scope
   */
  uuid: (key: string) => string;
};

export const SnippetScopeContext = createContext<SnippetScopeState | undefined>(
  undefined
);

/**
 * Shares variables, and the ids behind {{uuid:key}} placeholders, between
 * every <Snippet> inside it. The documentation layout gives each page one, so
 * that all of a page's examples use the same list id.
 *
 * Changing resetKey starts a new set of ids without remounting the children
 */
export function SnippetScope({
  vars,
  resetKey,
  children,
}: {
  vars?: SnippetVarValues;
  resetKey?: string;
  children?: ReactNode;
}): ReactElement {
  const parent = useContext(SnippetScopeContext);
  const uuids = useRef({ resetKey, values: new Map<string, string>() });

  if (uuids.current.resetKey !== resetKey) {
    uuids.current = { resetKey, values: new Map<string, string>() };
  }
  const values = uuids.current.values;

  const value = useMemo<SnippetScopeState>(
    () => ({
      vars: { ...parent?.vars, ...vars },
      uuid: (key: string) => {
        if (!values.has(key)) {
          values.set(key, uuidv4());
        }
        return values.get(key)!;
      },
    }),
    [parent, vars, values]
  );

  return (
    <SnippetScopeContext.Provider value={value}>
      {children}
    </SnippetScopeContext.Provider>
  );
}

/**
 * The id behind {{uuid:key}} placeholders in the nearest <SnippetScope>, so
 * that a page can show the same id outside its snippets (in an example
 * response, say). Outside a scope, a random id that stays the same for the
 * component
 */
export function useSnippetUuid(key: string): string {
  const scope = useContext(SnippetScopeContext);
  const [fallback] = useState(uuidv4);

  return scope ? scope.uuid(key) : fallback;
}
